import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { X } from 'lucide-react'
import {
    AdvisorSuggestion,
    acceptAdvisorSuggestion,
    dismissNudge,
    getDismissedNudges,
    getProjectAdvisorSuggestions,
} from '../../api/client'

// Stage 6: gentle nudge shown above the "no advisors" empty state on the
// Squad tab. Renders ONE suggestion (the first pending one for this
// project) with friendlier copy than the suggestions list. Dismissal
// persists via `users.prefs.dismissedNudges[projectId]` so it doesn't
// nag the user on every visit.
//
// We assume the host (ProjectDetailPage) only mounts this when the project
// has no advisors; we still gate locally on dismissed-nudges + presence of
// suggestions so the component is safe-by-itself.

interface Props {
    projectId: number
    onSquadChanged?: () => void
}

const nudgeName = (s: AdvisorSuggestion): string => {
    if (s.kind === 'persona' && s.persona) {
        const p = s.persona
        const nickname = (p.nickname || '').trim()
        const first = (p.first_name || '').trim()
        const last = (p.last_name || '').trim()
        const full = `${first} ${last}`.trim()
        return nickname || full || p.archetype || 'an advisor'
    }
    if (s.kind === 'draft' && s.draft) {
        const d = s.draft
        const first = (d.first_name || '').trim()
        const last = (d.last_name || '').trim()
        const full = `${first} ${last}`.trim()
        return full || d.archetype || 'a new advisor'
    }
    return 'an advisor'
}

const EmptyProjectNudge: React.FC<Props> = ({ projectId, onSquadChanged }) => {
    const [suggestion, setSuggestion] = useState<AdvisorSuggestion | null>(null)
    const [dismissed, setDismissed] = useState(false)
    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            setLoading(true)
            try {
                const [dismissedNudges, suggestions] = await Promise.all([
                    getDismissedNudges(),
                    getProjectAdvisorSuggestions(projectId),
                ])
                if (cancelled) return
                if (dismissedNudges.project_ids.includes(projectId)) {
                    setDismissed(true)
                    return
                }
                setSuggestion(suggestions[0] || null)
            } catch (e) {
                console.error('Failed to load empty-project nudge:', e)
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [projectId])

    const handleAccept = async () => {
        if (!suggestion || busy) return
        setBusy(true)
        try {
            await acceptAdvisorSuggestion(projectId, suggestion.id)
            setSuggestion(null)
            onSquadChanged?.()
        } catch (e) {
            console.error('Failed to accept advisor suggestion:', e)
        } finally {
            setBusy(false)
        }
    }

    const handleDismiss = async () => {
        if (busy) return
        setBusy(true)
        try {
            await dismissNudge(projectId)
            setDismissed(true)
        } catch (e) {
            console.error('Failed to dismiss nudge:', e)
        } finally {
            setBusy(false)
        }
    }

    if (loading || dismissed || !suggestion) return null

    const name = nudgeName(suggestion)
    const avatar =
        (suggestion.kind === 'persona' && suggestion.persona?.profile_picture_url) ||
        '/static/images/default_avatar.jpg'

    return (
        <AnimatePresence>
            <motion.div
                key="empty-project-nudge"
                initial={{ opacity: 0, scale: 0.95, y: -8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -8 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                style={{ transformOrigin: 'top center' }}
                className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4"
                data-testid="empty-project-nudge"
            >
                <div className="flex items-start gap-3">
                    <img
                        src={avatar}
                        alt={`${name} avatar`}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-grow min-w-0">
                        <div className="flex items-start justify-between gap-2">
                            <p className="text-sm text-gray-800">
                                Try chatting with{' '}
                                <span className="font-semibold">{name}</span> about
                                this project?
                            </p>
                            <motion.button
                                onClick={handleDismiss}
                                disabled={busy}
                                whileHover={{ scale: 1.1, rotate: 90 }}
                                whileTap={{ scale: 0.9 }}
                                transition={{
                                    type: 'spring',
                                    stiffness: 400,
                                    damping: 20,
                                }}
                                className="text-gray-400 hover:text-gray-700 flex-shrink-0"
                                aria-label="Dismiss suggestion"
                                data-testid="empty-project-nudge-dismiss"
                            >
                                <X size={16} />
                            </motion.button>
                        </div>
                        {suggestion.rationale && (
                            <p className="text-xs text-gray-600 italic mt-1">
                                {suggestion.rationale}
                            </p>
                        )}
                        <div className="mt-3">
                            <motion.button
                                onClick={handleAccept}
                                disabled={busy}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                transition={{
                                    type: 'spring',
                                    stiffness: 400,
                                    damping: 20,
                                }}
                                className="px-3 py-1 rounded-md bg-action text-plum text-sm hover:bg-action-dark disabled:opacity-50"
                                data-testid="empty-project-nudge-accept"
                            >
                                Add to squad
                            </motion.button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    )
}

export default EmptyProjectNudge
